import Container from 'components/container/container';
import LegalPost from 'components/legal/legal-post';
import Link from 'next/link';

import { LIBUM_URL, PRIVACY_MAILTO, SUPPORT_MAILTO } from 'shared/constants/globals';
import { LEGAL_DOCS } from 'shared/data/legal-docs';

const DOC = LEGAL_DOCS.find((d) => d.id === 'privacy')!;

export default function Privacy() {
  const title = DOC.title;
  const description = DOC.description;
  const keywords = DOC.keywords.join(',');
  const url = `${LIBUM_URL}/legal/${DOC.id}`;

  return (
    <Container
      title={title}
      description={description}
      keywords={keywords}
      url={url}
      socialCard="legal"
      breadcrumbs={[
        { name: 'Legal', url: `${LIBUM_URL}/legal` },
        { name: DOC.title, url },
      ]}
    >
      <div style={{ width: '100%' }}>
        <LegalPost document={DOC}>
          <p>
            We at Libum are committed to protecting your privacy. This Privacy Policy explains how we collect and use
            your personal information in relation to Libum products, services, events, and websites or applications that
            link to this Policy (together, the “Libum Products”). It also describes how you can control or exercise your
            rights related to your personal information.
          </p>
          <h1 className="anchor" id="responsible-libum-entity">
            <Link href="#responsible-libum-entity">1. Responsible Libum Entity</Link>
          </h1>
          <p>
            Libum is the controller of your Personal Data and responsible for the collection, processing and disclosure
            of your Personal Data as described in this Privacy Policy unless expressly specified otherwise.
          </p>
          <p>
            For detailed privacy information related to where the Libum customer is the controller, please reach out to
            the customer directly. We are not responsible for the privacy or data security practices of our customers,
            which may differ from those set forth in this Privacy Policy. Information regarding our privacy and data
            security practices as a processor of Personal Data on behalf of our customers can be found in the Libum Data
            Processing Addendum, available at{' '}
            <Link href="https://libum.io/legal/data/">https://libum.io/legal/data/</Link>.
          </p>
          <h1 className="anchor" id="what-information-we-collect-and-process">
            <Link href="#what-information-we-collect-and-process">2. What Information We Collect and Process</Link>
          </h1>
          <p>
            We process information about you while providing the Libum Products. Below, we outline the kinds of
            information we collect.
          </p>
          <p>
            <b>Information You Give Us</b>
            <br />
            <br />
            We collect information about you when you provide it to us directly.
          </p>
          <ul>
            <li>
              <span className="underline">Your Libum account.</span> We collect information about you when you submit it
              through the Libum Products, including when you create an account with us. This may include your name,
              email address, profile picture, company name, postal address, and phone number. This may also include your
              payment information when you purchase any of the Libum Products.
            </li>
            <li>
              <span className="underline">Support.</span> We collect information you provide to us when you open a
              support request or through other communication channels such as third-party social media sites. This may
              include your name, email address, title, company, and any other information you provide in your request.
            </li>
            <li>
              <span className="underline">Other information submitted by you.</span> We collect information about you
              upon submission through Libum Products, such as when you participate in any interactive features, or fill
              out a form on one of our websites. We collect information about you that you submit to us through the
              Libum Products, such as when you participate in any interactive features of the Libum Products or fill out
              a form on one of our websites. This information may include your name, email address, and mailing address.
            </li>
          </ul>
          <p>
            <b>Information We Collect Automatically</b>
            <br />
            <br />
            We automatically collect information about you when you access or use the Libum Products.
          </p>
          <ul>
            <li>
              <span className="underline">Your use of the Libum Products.</span> We collect information about how you
              use the Libum Products, such as your session date, time, and duration; the pages you viewed; and the page
              you visited before navigating to the products or websites.
            </li>
            <li>
              <span className="underline">Device Information.</span> We collect information about your computer or
              device when you access any of the Libum Products, such as the type of browser you use, your IP address,
              and the location of your device.
            </li>
            <li>
              <span className="underline">Cookies and similar technologies.</span> We use cookies and similar
              technologies (like web beacons and JavaScript) to collect information about your interactions with the
              Libum Products, including identifiers, usage data, session information, links clicked, and pages visited.
            </li>
          </ul>
          <p>
            <b>Information We Collect from Other Sources</b>
            <br />
            <br />
            We may also obtain information about you from other sources.
          </p>
          <ul>
            <li>
              <span className="underline">Other services linked to your account.</span> We may collect information about
              you when you link your Libum account with other services. For example, if you create or log into your
              Libum account using your Google Apps credentials via single sign-on, we will have access to certain
              information such as your name and email address, as authorized in your Google Apps profile settings.
            </li>
            <li>
              <span className="underline">Third parties.</span> We may receive information about you from third parties
              that provide business information to us or from public sources. This information may include things like
              your name, title, email address, phone number, and social-media profile. We may combine this information
              with information we collect through other means described above.
            </li>
          </ul>
          <h1 className="anchor" id="how-we-use-personal-data">
            <Link href="#how-we-use-personal-data">3. How We Use Personal Data</Link>
          </h1>
          <p>We use information about you for the following purposes.</p>
          <ul>
            <li>
              <span className="underline">To provide and support your use of the Libum Products.</span> We use
              information about you to provide you with access to the Libum Products, including to create and manage
              your Libum accounts, process transactions with you, and send you invoices, as well as to provide you with
              customer support.
            </li>
            <li>
              <span className="underline">For research and to improve the Libum Products.</span> We use information
              about you, including information about your use of Libum Products, to monitor and analyze trends, usage,
              and other activities in connection with the Libum Products so that we can continually improve them. We may
              also link or combine information about you with information we get from others to help understand your
              needs and provide you with better service.
            </li>
            <li>
              <span className="underline">To communicate with you.</span> We use information about you to communicate
              with you, including to send you technical notices, product updates, security alerts, support
              communications, and administrative messages about the Libum Products (including important changes to the
              Libum Products). This also includes sending you information about your account and data, such as when we
              update our terms or when we materially change how we use your data.
            </li>
            <li>
              <span className="underline">For security.</span> We use information about you to maintain and increase the
              security of the Libum Products, including to detect, investigate, and prevent fraud and other illegal
              activities from occurring, as well as to protect the rights and property of Libum, our customers, and
              third parties.
            </li>
            <li>
              <span className="underline">To market and promote the Libum Products.</span> We use information about you
              to send you promotional messages and to show you advertisements. This may also include using information
              about you to create more personalized advertising and suggest Libum Products that may be of interest to
              you.
            </li>
            <li>
              <span className="underline">To comply with legal obligations.</span> We may use information about you to
              comply with our legal requirements. For example, we may process and retain information about your payments
              to us for tax and accounting purposes.
            </li>
            <li>
              <span className="underline">With your consent.</span> Finally, we use information about you for any other
              purposes that you have consented to. For example, if you agree to be named as a Featured Customer, we
              might post information about you on one of our public websites.
            </li>
          </ul>
          <h1 className="anchor" id="how-we-share-personal-data">
            <Link href="#how-we-share-personal-data">4. How We Share Personal Data</Link>
          </h1>
          <p>We may share your personal information as follows.</p>
          <ul>
            <li>
              <span className="underline">Service providers.</span> We use third-party service providers who work on our
              behalf, including to provide hosting services, authentication services, and cybersecurity to improve our
              security posture, which may require us to share your personal information.
            </li>
            <li>
              <span className="underline">For legal reasons.</span> In rare cases, we may share information about you in
              response to a request for information if we believe disclosure is permitted or required by an applicable
              law, regulation, or legal process, including to comply with a subpoena or applicable court order. Further,
              we may share your personal information with any person to whom disclosure is necessary to enable us to
              enforce our rights under this Privacy Policy or under any agreement we enter with you or to protect the
              rights, property, or safety of Libum or third parties.
            </li>
            <li>
              <span className="underline">Marketing and analytics.</span> We may share your personal information with
              analytics and search-engine providers that assist us in the improvement and optimization of our websites.
            </li>
            <li>
              <span className="underline">Social Media.</span> Some of our websites may contain social-sharing widgets
              or features that let you share information you find on our websites on third-party sites, including, for
              example, Twitter, LinkedIn, and YouTube. By doing so, you authorize us to facilitate this sharing of
              information, and you understand that the use of shared information will be governed by the social media
              provider&apos;s privacy notice.
            </li>
            <li>
              <span className="underline">Consent.</span> We may share your personal information with your consent or at
              your direction.
            </li>
          </ul>
          <h1 className="anchor" id="security">
            <Link href="#security">5. Security</Link>
          </h1>
          <p>
            The security of your personal information is incredibly important to us. We use a variety of security
            technologies and procedures to help protect your personal information from unauthorized access, use or
            disclosure. All personal information is protected using appropriate physical, technical and organizational
            measures. For more security information at Libum, please visit{' '}
            <Link href="https://trust.libum.io/">https://trust.libum.io/</Link>.
          </p>
          <h1 className="anchor" id="retention">
            <Link href="#retention">6. Retention</Link>
          </h1>
          <p>
            We only keep your personal information for as long as necessary to fulfill the purposes we collected it for,
            after which it will be deleted or archived unless we are required to keep it to comply with our legal
            obligations or for another legitimate and lawful purpose. To determine the appropriate retention period for
            personal information, we consider several factors, including:
          </p>
          <ul>
            <li>the terms of our agreements with you;</li>
            <li>our legitimate interested (as outlined in this Privacy Policy);</li>
            <li>our legal obligations; and</li>
            <li>the nature of your personal information.</li>
          </ul>
          <p>
            In some cases, we may anonymize your personal information so that you are no longer identifiable, in which
            case we may use the resulting information without further notice to you.
          </p>
          <h1 className="anchor" id="your-choices">
            <Link href="#your-choices">7. Your Choices</Link>
          </h1>
          <p>You have choices about how Libum collects and uses your personal information.</p>
          <ul>
            <li>
              <span className="underline">Account information.</span> Our customers may access, update, or change
              personal information they have provided to us by logging into the relevant Libum application or emailing
              us at <Link href={SUPPORT_MAILTO}>support@libum.io</Link>. Subject to the terms of their agreements with
              us, customers may deactivate their accounts by emailing us at{' '}
              <Link href={SUPPORT_MAILTO}>support@libum.io</Link>, but we may retain certain personal information as
              necessary to comply with our legal obligations or for legitimate business purposes, such as to resolve
              disputes or enforce our agreements. We may also retain cached or archived copies of personal information
              for a certain period.
            </li>
            <li>
              <span className="underline">Advertising.</span> You may opt out of receiving promotional emails from Libum
              by following the instructions in those emails or by emailing{' '}
              <Link href={SUPPORT_MAILTO}>support@libum.io</Link>.
            </li>
          </ul>
          <h1 className="anchor" id="supplemental-notice-for-the-united-states">
            <Link href="#supplemental-notice-for-the-united-states">8. Supplemental Notice for the United States</Link>
          </h1>
          <p>
            This section provides additional details about the personal information we collect about individuals and the
            rights afforded to them under various applicable U.S. state data-protection and privacy laws, including the
            California Consumer Privacy Act, as amended (CCPA), the Colorado Privacy Act (CPA), the Connecticut Data
            Privacy Act (CTDPA), the Utah Consumer Privacy Act (UCPA), and the Virginia Consumer Data Protection Act
            (VCDPA).
          </p>
          <ul>
            <li>
              <span className="underline">Right to access.</span> You have the right to request that we disclose to you
              in a portable format the personal information we collect, use, disclose, share, and sell about you.
            </li>
            <li>
              <span className="underline">Right to correct.</span> You have the right to correct errors in your personal
              information.
            </li>
            <li>
              <span className="underline">Right to delete.</span> You have the right to request that we delete your
              personal information that we&apos;ve collected.
            </li>
            <li>
              <span className="underline">Right to update.</span> You have the right to request that inaccurate personal
              information we hold about you be corrected.
            </li>
            <li>
              <span className="underline">Right to opt out.</span> You have the right to opt out of behavioral or
              targeted advertising, automated profiling, and sales of personal information.
            </li>
            <li>
              <span className="underline">Right to restrict the use and disclosure of your sensitive information.</span>{' '}
              You have the right to request that we limit our use and disclosure of your sensitive personal information.
            </li>
            <li>
              <span className="underline">Right to nondiscrimination.</span> You have the right not to receive
              discriminatory treatment because you&apos;ve exercised any of your rights under the CCPA.
            </li>
          </ul>
          <p>
            <b>Your rights related to Customer Data</b>
            <br />
            <br />
            As described above, we may also process Personal Data in the role of a processor (see “Responsible Libum
            Entity” section above). If your data has been submitted to us by a Libum customer and you wish to exercise
            any rights you may have under applicable data protection laws, please inquire with our customer directly.
            Because we may only access our customer&apos;s data upon instruction from the respective customer, if you
            wish to make your request directly to us, please provide the name of the Libum customer who submitted your
            data when you contact us. We will refer your request to that customer and will support them as needed in
            responding to your request within a reasonable timeframe.
          </p>
          <p>
            If you or your authorized agent wishes to exercise any of these rights, please contact us as outlined in the
            “Contact Us” section below. Please note that we may ask you or your agent to provide us with additional
            information to confirm your identity.
          </p>
          <p>
            If you submit a request to exercise one of the above rights and you disagree with our decision regarding
            your request, you may have the right to appeal our decision under applicable law. To do so, please reply to
            our response.
          </p>
          <p>
            <b>Categories of Personal Data Collected</b>
            <br />
            <br />
            The personal information that we may have collected in the past 12 months fall into the following categories
            specifically established under the CCPA:
          </p>
          <ul>
            <li>
              Identifiers such as a real name, postal address, unique personal identifier, online identifier, internet
              protocol address, and email address.
            </li>
            <li>
              Information under Cal. Civ. Code §1798.80(e), such as your name, address, telephone number, or any
              financial information.
            </li>
            <li>Commercial information, such as information related to products or services you&apos;ve purchased.</li>
            <li>
              Internet or other electronic network activity information, such information regarding your interaction
              with the Libum Products.
            </li>
            <li>Geolocation data.</li>
            <li>Audio, electronic, visual, or similar information, such as audio recordings of calls with you.</li>
            <li>Inferences drawn on the information above, such as aggregated metrics.</li>
            <li>
              Account log-in or credit card number in combination with your credentials allowing access to your account.
            </li>
          </ul>
          <p>
            For more information about the categories of personal information we collect, please see the “What
            Information We Collect and Process” section above.
          </p>
          <p>
            <b>Categories of Personal Data Disclosed for a Business Purpose</b>
            <br />
            <br />
            The personal information that we may have disclosed for a business purpose (including to our service
            providers) in the past 12 months fall into the following categories specifically established under the CCPA:
          </p>
          <ul>
            <li>
              Identifiers such as a real name, postal address, unique personal identifier, online identifier, internet
              protocol address, and email address.
            </li>
            <li>
              Information under Cal. Civ. Code §1798.80(e), such as your name, address, telephone number, or any
              financial information.
            </li>
            <li>Commercial information, such as information related to products or services you’ve purchased.</li>
            <li>
              Internet or other electronic network activity information, such information regarding your interaction
              with the Libum Products.
            </li>
            <li>Geolocation data.</li>
            <li>Audio, electronic, visual, or similar information, such as audio recordings of calls with you.</li>
            <li>Inferences drawn on the information above, such as aggregated metrics.</li>
            <li>
              Account log-in or credit card number in combination with your credentials allowing access to your account.
            </li>
          </ul>
          <p>
            For more information about the categories of personal information we disclose to other parties, please see
            the “How We Share Personal Data” section above.
          </p>
          <p>
            <b>No Sale or Sharing of Personal Data</b>
            <br />
            <br />
            We do not sell or share (for the purpose of cross-context behavioral advertising) your personal information,
            as those terms are defined under the CCPA.
          </p>
          <h1 className="anchor" id="changes-to-this-privacy-policy">
            <Link href="#changes-to-this-privacy-policy">9. Changes to this Privacy Policy</Link>
          </h1>
          <p>
            We may occasionally update this Privacy Policy. The date at the top of this Privacy Policy indicates when it
            was last revised. The latest effective Privacy Policy can be accessed at{' '}
            <Link href="https://libum.io/legal/privacy/">https://libum.io/legal/privacy/</Link>.
          </p>
          <h1 className="anchor" id="contact-us">
            <Link href="#contact-us">10. Contact Us</Link>
          </h1>
          <p>
            If you have any questions or concerns about this Privacy Policy, please contact us at{' '}
            <Link href={PRIVACY_MAILTO}>privacy@libum.io</Link>.
          </p>
        </LegalPost>
      </div>
    </Container>
  );
}
